import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  motion,
  useInView,
  useScroll,
  useSpring,
  useTransform,
  animate,
} from "framer-motion";
import {
  ArrowRight,
  ChevronDown,
  FileCheck2,
  MessageCircle,
  ShieldCheck,
  Sparkles,
  Zap,
} from "lucide-react";
import { buildWhatsappUrl } from "@/lib/whatsapp";
import { RotatingWord } from "@/components/landing/RotatingWord";
import { HeroCarousel } from "@/components/landing/HeroCarousel";

/* -------------------------------------------------------------------------- */
/*  Animated price counter                                                    */
/* -------------------------------------------------------------------------- */
const PriceCounter = ({ value, duration = 1.4 }: { value: number; duration?: number }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return controls.stop;
  }, [inView, value, duration]);

  return (
    <span
      ref={ref}
      role="text"
      aria-label={`${value} dirhams`}
    >
      <span aria-hidden="true">{display}</span>
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/*  Stagger title — split into words                                          */
/* -------------------------------------------------------------------------- */
const StaggerTitle = ({ text, accent }: { text: string; accent?: string }) => {
  const words = text.split(" ");
  return (
    <span className="inline-block">
      {words.map((w, i) => (
        <motion.span
          key={`${w}-${i}`}
          initial={{ y: 24 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.5, delay: i * 0.07, ease: [0.22, 1, 0.36, 1] }}
          style={{ willChange: "transform" }}
          className={`inline-block ${w === accent ? "bg-gradient-warm bg-clip-text text-transparent" : ""}`}
        >
          {w}
          {i < words.length - 1 && "\u00A0"}
        </motion.span>
      ))}
    </span>
  );
};

/* -------------------------------------------------------------------------- */
/*  Animated blueprint SVG — enseigne qui se dessine                          */
/* -------------------------------------------------------------------------- */
const BlueprintSign = ({ ariaLabel }: { ariaLabel: string }) => {
  const ref = useRef<SVGSVGElement>(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });

  const drawn = inView ? "show" : "hidden";

  const pathTransition = (delay = 0) => ({
    duration: 1.4,
    ease: [0.22, 1, 0.36, 1] as const,
    delay,
  });

  return (
    <svg
      ref={ref}
      viewBox="0 0 480 360"
      className="size-full"
      role="img"
      aria-label={ariaLabel}
    >
      {/* Grille blueprint */}
      <defs>
        <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
          <path
            d="M 20 0 L 0 0 0 20"
            fill="none"
            stroke="hsl(var(--primary))"
            strokeOpacity="0.08"
            strokeWidth="0.5"
          />
        </pattern>
        <linearGradient id="signFill" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.06" />
          <stop offset="100%" stopColor="hsl(var(--primary-glow))" stopOpacity="0.1" />
        </linearGradient>
      </defs>

      <rect width="480" height="360" fill="url(#grid)" />

      {/* Cadre principal de l'enseigne */}
      <motion.rect
        x="80"
        y="100"
        width="320"
        height="120"
        rx="8"
        fill="url(#signFill)"
        stroke="hsl(var(--primary))"
        strokeWidth="2"
        initial={{ pathLength: 0, opacity: 0 }}
        variants={{
          hidden: { pathLength: 0, opacity: 0 },
          show: { pathLength: 1, opacity: 1 },
        }}
        animate={drawn}
        transition={pathTransition(0)}
      />

      {/* Texte stylisé "CONCEPTIONS" */}
      <motion.text
        x="240"
        y="170"
        textAnchor="middle"
        fontFamily="Space Grotesk, Inter, sans-serif"
        fontSize="32"
        fontWeight="700"
        fill="hsl(var(--primary))"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 1.2 }}
      >
        CONCEPTIONS
      </motion.text>
      <motion.text
        x="240"
        y="195"
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="13"
        fontWeight="500"
        fill="hsl(var(--primary-glow))"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5, delay: 1.4 }}
      >
        .ma — atelier digital
      </motion.text>

      {/* Repères de fixation aux 4 coins */}
      {[
        [80, 100],
        [400, 100],
        [80, 220],
        [400, 220],
      ].map(([cx, cy], i) => (
        <motion.circle
          key={`fix-${i}`}
          cx={cx}
          cy={cy}
          r="5"
          fill="hsl(var(--background))"
          stroke="hsl(var(--primary))"
          strokeWidth="2"
          initial={{ scale: 0, opacity: 0 }}
          animate={inView ? { scale: 1, opacity: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.5 + i * 0.1, ease: "backOut" }}
        />
      ))}

      {/* Cotation horizontale — largeur */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 1.6 }}
      >
        <line x1="80" y1="70" x2="400" y2="70" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <line x1="80" y1="62" x2="80" y2="78" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <line x1="400" y1="62" x2="400" y2="78" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <rect x="215" y="56" width="50" height="18" fill="hsl(var(--background))" />
        <text
          x="240"
          y="69"
          textAnchor="middle"
          fontFamily="Space Grotesk, monospace"
          fontSize="12"
          fontWeight="600"
          fill="hsl(var(--secondary))"
        >
          2400 mm
        </text>
      </motion.g>

      {/* Cotation verticale — hauteur */}
      <motion.g
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : {}}
        transition={{ duration: 0.6, delay: 1.75 }}
      >
        <line x1="430" y1="100" x2="430" y2="220" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <line x1="422" y1="100" x2="438" y2="100" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <line x1="422" y1="220" x2="438" y2="220" stroke="hsl(var(--secondary))" strokeWidth="1" />
        <rect x="418" y="151" width="40" height="18" fill="hsl(var(--background))" />
        <text
          x="438"
          y="164"
          textAnchor="middle"
          fontFamily="Space Grotesk, monospace"
          fontSize="12"
          fontWeight="600"
          fill="hsl(var(--secondary))"
        >
          900 mm
        </text>
      </motion.g>

      {/* Indication LED + matériau */}
      <motion.g
        initial={{ opacity: 0, y: 8 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 1.9 }}
      >
        <line
          x1="120"
          y1="220"
          x2="120"
          y2="265"
          stroke="hsl(var(--accent))"
          strokeWidth="1"
          strokeDasharray="3 3"
        />
        <circle cx="120" cy="220" r="3" fill="hsl(var(--accent))" />
        <rect x="80" y="265" width="180" height="28" rx="6" fill="hsl(var(--accent) / 0.1)" stroke="hsl(var(--accent))" strokeWidth="1" />
        <text
          x="170"
          y="284"
          textAnchor="middle"
          fontFamily="Space Grotesk, monospace"
          fontSize="11"
          fontWeight="600"
          fill="hsl(var(--accent))"
        >
          LED 12V · Alucobond 4mm
        </text>
      </motion.g>

      {/* Tag prix flottant */}
      <motion.g
        initial={{ opacity: 0, scale: 0.7 }}
        animate={inView ? { opacity: 1, scale: 1 } : {}}
        transition={{ duration: 0.5, delay: 2.1, ease: "backOut" }}
      >
        <rect x="300" y="265" width="100" height="40" rx="20" fill="hsl(var(--primary))" />
        <text
          x="350"
          y="282"
          textAnchor="middle"
          fontFamily="Inter, sans-serif"
          fontSize="9"
          fontWeight="600"
          fill="hsl(var(--primary-foreground))"
          opacity="0.85"
        >
          DOSSIER #2026
        </text>
        <text
          x="350"
          y="298"
          textAnchor="middle"
          fontFamily="Space Grotesk, sans-serif"
          fontSize="14"
          fontWeight="700"
          fill="hsl(var(--primary-foreground))"
        >
          ✓ CNC-ready
        </text>
      </motion.g>
    </svg>
  );
};

/* -------------------------------------------------------------------------- */
/*  Hero v2                                                                   */
/* -------------------------------------------------------------------------- */
export const HeroV2 = () => {
  const { t, i18n } = useTranslation("landing");
  const containerRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const rawY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const parallaxY = useSpring(rawY, {
    stiffness: 80,
    damping: 20,
  });

  const rotatingWords = (t("heroes.title_v2_rotating", { returnObjects: true }) as string[]) || [];
  const rotatingHighlights = useMemo(() => {
    const pool = [...rotatingWords];
    const picks: string[] = [];
    const count = Math.min(5, pool.length);
    for (let i = 0; i < count; i++) {
      const idx = Math.floor(Math.random() * pool.length);
      picks.push(pool.splice(idx, 1)[0]);
    }
    return picks;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  return (
    <section
      ref={containerRef}
      className="bg-mesh-hero section-surface section-y-sm"
      style={{ contain: "layout style paint" }}
    >
      {/* Halos décoratifs supplémentaires */}
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/4 size-[320px] rounded-full opacity-60 blur-3xl sm:size-[420px] lg:size-[520px]"
        style={{ background: "radial-gradient(closest-side, hsl(var(--primary-glow) / 0.45), transparent 70%)" }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 top-32 hidden size-[420px] rounded-full opacity-40 blur-3xl sm:block"
        style={{ background: "radial-gradient(closest-side, hsl(var(--secondary) / 0.35), transparent 70%)" }}
      />

      <div className="container relative grid items-center gap-8 sm:gap-10 lg:gap-12 xl:grid-cols-[1.1fr_1fr] xl:gap-14">
        {/* Colonne texte */}
        <div className="min-w-0 space-y-5 sm:space-y-7">
          {/* Badge CNC-Ready glow */}
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-primary backdrop-blur animate-glow-pulse sm:px-4 sm:text-xs">
              <Sparkles className="size-3.5" />
              {t("heroes.badge_new_2026")}
            </span>
          </motion.div>

          {/* H1 fluid stagger */}
          <h1 className="text-fluid-h1 font-display font-extrabold text-foreground">
            <StaggerTitle text={t("heroes.title_v2_line1")} />
            <br />
            <StaggerTitle text={t("heroes.title_v2_line2")} accent={t("heroes.title_v2_line2_accent")} />
            <br />
            <span className="text-foreground/70">{t("heroes.title_v2_line3_prefix")}</span>
            <RotatingWord
              words={rotatingWords}
              dir={i18n.language?.startsWith("ar") ? "rtl" : "ltr"}
              className="bg-gradient-aurora-text bg-clip-text text-transparent font-extrabold"
            />
            <span className="text-foreground/70">{t("heroes.title_v2_line3_suffix")}</span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.55 }}
            className="text-fluid-lead max-w-xl text-muted-foreground"
          >
            {t("heroes.subtitle_price_v2_prefix")}
            <strong className="text-price">
              <PriceCounter value={49} /> DH
            </strong>
            {t("heroes.subtitle_price_v2_middle")}
            <strong className="text-price">
              <PriceCounter value={390} /> DH
            </strong>
            {t("heroes.subtitle_price_v2_suffix")}
          </motion.p>

          {/* CTA — full-width mobile, inline desktop */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center"
          >
            <Link
              to="/devis"
              className="press-scale btn-aurora cta-shine group inline-flex h-12 w-full items-center justify-center gap-2 rounded-full px-8 text-base font-bold text-primary-foreground shadow-warm-lg transition-all hover:scale-[1.02] hover:bg-primary/90 sm:w-auto"
            >
              <span className="relative z-10 inline-flex items-center gap-2">
                {t("heroes.cta_compose_quote")}
                <ArrowRight className="size-4 rtl-flip transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
            <a
              href={buildWhatsappUrl(t("heroes.wa_default_message"))}
              target="_blank"
              rel="noopener noreferrer"
              className="press-scale cta-shine inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-success px-10 text-lg font-bold text-success-foreground shadow-warm-lg transition-transform hover:scale-[1.05] sm:w-auto"
            >
              <MessageCircle className="size-5" />
              {t("heroes.cta_whatsapp_short")}
            </a>
          </motion.div>

          {/* Trust strip */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="grid grid-cols-1 gap-2 pt-2 text-xs font-medium text-muted-foreground sm:flex sm:flex-wrap sm:items-center sm:gap-x-6 sm:gap-y-2"
          >
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="size-4 shrink-0 text-success" />
              {t("heroes.trust_zero_refusal")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Zap className="size-4 shrink-0 text-secondary" />
              {t("heroes.trust_response_1h")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <FileCheck2 className="size-4 shrink-0 text-primary" />
              {t("heroes.trust_sources")}
            </span>
          </motion.div>
        </div>

        {/* Colonne SVG blueprint avec parallax */}
        <motion.div style={{ y: parallaxY, willChange: "transform", transform: "translateZ(0)" }} className="relative">
          <div
            aria-hidden
            className="absolute inset-0 translate-x-3 translate-y-3 rounded-3xl bg-gradient-warm opacity-20 blur-2xl"
          />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            style={{ contain: "layout style" }}
          >
            <HeroCarousel />
          </motion.div>

          {/* Mini carte flottante "atelier validé" */}
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="card-warm absolute -bottom-4 -left-4 z-20 hidden items-center gap-2 p-3 sm:flex"
          >
            <div className="grid size-9 place-items-center rounded-full bg-success/15 text-success">
              <MessageCircle className="size-4" />
            </div>
            <div className="text-xs">
              <div className="font-display font-bold text-foreground">{t("dossier_mock.atelier_validated")}</div>
              <div className="text-muted-foreground">{t("dossier_mock.two_min_ago")}</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Flèche scroll douce */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.6 }}
        className="absolute inset-x-0 bottom-4 hidden justify-center md:flex"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="grid size-10 place-items-center rounded-full border border-border bg-card/70 text-primary backdrop-blur"
        >
          <ChevronDown className="size-5" />
        </motion.div>
      </motion.div>
    </section>
  );
};
